import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api/client';
import { getCheckouts } from '../api/checkouts';

const STATS = [
  { key:'books',           label:'total_books',      accent:'var(--accent)',   iconBg:'var(--accent-bg)',   iconColor:'var(--accent)',   icon:'ti-books' },
  { key:'physical_copies', label:'physical_copies',  accent:'var(--purple)',   iconBg:'var(--purple-bg)',   iconColor:'var(--purple)',   icon:'ti-copy' },
  { key:'members',         label:'total_members',    accent:'var(--success)',  iconBg:'var(--success-bg)',  iconColor:'var(--success)',  icon:'ti-users' },
  { key:'active_checkouts',label:'active_checkouts', accent:'var(--warning)',  iconBg:'var(--warning-bg)', iconColor:'var(--warning)',  icon:'ti-arrows-exchange' },
  { key:'overdue',         label:'overdue',           accent:'var(--danger)',   iconBg:'var(--danger-bg)',   iconColor:'var(--danger)',   icon:'ti-alert-triangle' },
];

const PIE_COLORS = ['var(--success)','var(--accent)','var(--danger)'];

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',boxShadow:'var(--shadow-md)',fontSize:12}}>
      {label && <p style={{color:'var(--text-muted)',marginBottom:4,marginTop:0}}>{label}</p>}
      <p style={{color:'var(--accent)',fontWeight:600,margin:0}}>{payload[0].value}</p>
    </div>
  );
};

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background:'var(--bg-card)',
      border:'1px solid var(--border)',
      borderRadius:12,
      boxShadow:'var(--shadow-sm)',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardHeader({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <div style={{padding:'14px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
      <span style={{width:3,height:14,borderRadius:2,background:accent,display:'inline-block',flexShrink:0}} />
      <span style={{fontSize:13,fontWeight:600,color:'var(--text-secondary)'}}>{children}</span>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: stats }       = useQuery({ queryKey:['stats'],            queryFn: () => api.get('/stats') });
  const { data: active }      = useQuery({ queryKey:['active-checkouts'], queryFn: () => getCheckouts({ status:'active',  limit:5 }) });
  const { data: overdueData } = useQuery({ queryKey:['overdue-checkouts'],queryFn: () => getCheckouts({ status:'overdue', limit:4 }) });
  const s = (stats as any)?.data;

  const collStats  = (s?.collection_stats||[]).map((c: any) => ({
    name: c.name?.length > 12 ? c.name.slice(0,12)+'…' : (c.name||'სხვა'),
    count: Number(c.count),
  }));
  const monthlyStats = (s?.monthly_stats||[]).map((m: any) => ({
    name: m.month?.slice(5) || '',
    count: Number(m.count),
  }));
  const pieData = [
    { name:'ხელმისაწვდომი', value: Math.max(0,(s?.books||0)-(s?.active_checkouts||0)) },
    { name:'გაცემული',       value: s?.active_checkouts||0 },
    { name:'ვადაგადაც.',     value: s?.overdue||0 },
  ].filter(d => d.value > 0);

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>

      {/* ── page title ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:19,fontWeight:700,color:'var(--text-primary)',margin:0,letterSpacing:'-0.3px'}}>{t('dashboard')}</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',margin:'3px 0 0'}}>
            {new Date().toLocaleDateString('ka-GE',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
          </p>
        </div>
        <button
          onClick={() => navigate('/checkouts')}
          style={{
            display:'flex',alignItems:'center',gap:7,
            padding:'8px 16px',
            background:'var(--accent)',border:'none',borderRadius:9,
            color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',
            boxShadow:'0 2px 8px var(--accent-glow)',
            transition:'opacity 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity='0.88')}
          onMouseLeave={e => (e.currentTarget.style.opacity='1')}>
          <i className="ti ti-arrows-exchange" style={{fontSize:15}} />
          ახალი გაცემა
        </button>
      </div>

      {/* ── stat cards ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:16}}>
        {STATS.map(st => (
          <Card key={st.key} style={{padding:16,position:'relative',overflow:'hidden'}}>
            {/* accent top line */}
            <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:st.accent,borderRadius:'12px 12px 0 0'}} />
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
              <div style={{width:34,height:34,borderRadius:9,background:st.iconBg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <i className={`ti ${st.icon}`} style={{fontSize:17,color:st.iconColor}} />
              </div>
            </div>
            <div style={{fontSize:26,fontWeight:700,color:'var(--text-primary)',letterSpacing:'-0.5px',lineHeight:1}}>
              {s?.[st.key]?.toLocaleString() ?? '—'}
            </div>
            <div style={{fontSize:11,color:'var(--text-faint)',marginTop:6,fontWeight:500}}>
              {t(st.label)}
            </div>
          </Card>
        ))}
      </div>

      {/* ── charts row ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>

        {/* Monthly checkouts */}
        <Card style={{gridColumn:'span 2'}}>
          <CardHeader accent="var(--accent)">გაცემები თვეების მიხედვით</CardHeader>
          <div style={{padding:'16px 16px 12px'}}>
            {monthlyStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={monthlyStats} margin={{top:0,right:0,bottom:0,left:-20}}>
                  <XAxis dataKey="name" tick={{fill:'var(--text-faint)',fontSize:11}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fill:'var(--text-faint)',fontSize:11}} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{fill:'var(--bg-hover)'}} />
                  <Bar dataKey="count" fill="var(--accent)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-dimmed)',fontSize:13}}>
                მონაცემი არ არის
              </div>
            )}
          </div>
        </Card>

        {/* Status pie */}
        <Card>
          <CardHeader accent="var(--success)">სტატუსის განაწილება</CardHeader>
          <div style={{padding:'12px 16px 16px'}}>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={130}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={36} outerRadius={58} dataKey="value" paddingAngle={3} strokeWidth={0}>
                      {pieData.map((_,i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:'flex',flexDirection:'column',gap:6,marginTop:10}}>
                  {pieData.map((d,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:8,fontSize:11}}>
                      <div style={{width:8,height:8,borderRadius:2,background:PIE_COLORS[i],flexShrink:0}} />
                      <span style={{color:'var(--text-muted)',flex:1}}>{d.name}</span>
                      <span style={{color:'var(--text-secondary)',fontWeight:600}}>{d.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{height:130,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-dimmed)',fontSize:13}}>
                იტვირთება...
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── bottom row ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>

        {/* Collections bar */}
        <Card>
          <CardHeader accent="var(--warning)">კოლექციები</CardHeader>
          <div style={{padding:'12px 16px 16px'}}>
            {collStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={collStats} layout="vertical" margin={{top:0,right:8,bottom:0,left:60}}>
                  <XAxis type="number" tick={{fill:'var(--text-faint)',fontSize:10}} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{fill:'var(--text-secondary)',fontSize:10}} axisLine={false} tickLine={false} width={60} />
                  <Tooltip content={<ChartTooltip />} cursor={{fill:'var(--bg-hover)'}} />
                  <Bar dataKey="count" fill="var(--warning)" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{height:160,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-dimmed)',fontSize:13}}>
                მონაცემი არ არის
              </div>
            )}
          </div>
        </Card>

        {/* Recent + overdue */}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <Card style={{flex:1,minHeight:0}}>
            <CardHeader accent="var(--accent)">{t('recent_checkouts')}</CardHeader>
            {(active as any)?.data?.checkouts?.slice(0,4).map((c: any) => (
              <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 16px',borderBottom:'1px solid var(--border)'}}>
                <div style={{width:32,height:32,borderRadius:8,background:'var(--accent-bg)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <i className="ti ti-book" style={{fontSize:14,color:'var(--accent)'}} />
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:500,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.title}</div>
                  <div style={{fontSize:11,color:'var(--text-faint)'}}>{c.member_name}</div>
                </div>
                <span style={{fontSize:10,color:'var(--text-faint)',flexShrink:0,fontFamily:'monospace'}}>
                  {new Date(c.due_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})}
                </span>
              </div>
            ))}
          </Card>

          <Card>
            <CardHeader accent="var(--danger)">ვადაგადაცილებული</CardHeader>
            {(overdueData as any)?.data?.checkouts?.length === 0 && (
              <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--success)'}}>
                <i className="ti ti-circle-check" style={{fontSize:16}} />
                {t('no_overdue')}
              </div>
            )}
            {(overdueData as any)?.data?.checkouts?.slice(0,3).map((c: any) => (
              <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 16px',borderBottom:'1px solid var(--border)'}}>
                <div style={{width:32,height:32,borderRadius:8,background:'var(--danger-bg)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <i className="ti ti-clock-exclamation" style={{fontSize:14,color:'var(--danger)'}} />
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:500,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.title}</div>
                  <div style={{fontSize:11,color:'var(--text-faint)'}}>{c.member_name}</div>
                </div>
                <span style={{fontSize:10,color:'var(--danger)',fontWeight:600,flexShrink:0,fontFamily:'monospace'}}>
                  {new Date(c.due_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})}
                </span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
